import { useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Label,
} from 'recharts';
import {
  buildAccountMix,
  buildAccountMixFromReal,
  buildRothTaxSeries,
  buildRothTaxSeriesFromReal,
  CHART_DISCLAIMER,
} from '../../utils/chartApprox';
import { T, fmtMoney, resolveChartColors, type ChartColors } from '../theme';
import { AsteriskNote, LegendSwatch, ttStyle } from '../primitives';
import { planStartYearOf, type OptimizationVM, type RothVM } from '../../formatters/deckContent';
import type { ReportEnrichment, TaxSeriesPoint, WebReportPayload } from '../../types/webReport';

interface Props {
  vm: OptimizationVM;
  payload: WebReportPayload;
  enrichment: ReportEnrichment | null;
  optimized: boolean;
  /** When set: fixed-size charts (print) with animations disabled. */
  width?: number;
}

/** Framing 3 — Roth: stacked account bars (before/after) + tax-line graph. */
export function BeforeAfterPanel({ vm, payload, enrichment, optimized, width }: Props) {
  const colors = useMemo(() => resolveChartColors(), []);
  const roth = vm.roth;

  const savings = enrichment?.household.savings;
  const hasMix = !!roth && !!savings && savings.total > 0;

  const mix = useMemo(() => {
    if (!hasMix || !roth || !savings) return null;
    // Real end-of-plan mix from SIPS (schema v8) when present; older reports
    // fall back to the shift-and-scale synthesis. The baseline end-of-plan mix
    // (schema v9) is what makes the two bars like for like.
    if (vm.endOfPlanAccountMix) {
      return buildAccountMixFromReal({
        savings: { taxable: savings.taxable, taxDeferred: savings.taxDeferred, taxFree: savings.taxFree },
        endMix: vm.endOfPlanAccountMix,
        baselineMix: vm.baselineEndOfPlanAccountMix,
      });
    }
    return buildAccountMix({
      savings: { taxable: savings.taxable, taxDeferred: savings.taxDeferred, taxFree: savings.taxFree },
      conversionsTotal: roth.total,
      endOfPlanAssets: enrichment?.metrics.optimized?.endOfPlanAssets,
    });
  }, [hasMix, roth, savings, enrichment, vm.endOfPlanAccountMix, vm.baselineEndOfPlanAccountMix]);

  if (!roth) return null;

  const animated = width == null;

  let barSection: React.ReactNode = null;
  if (mix) {
    const before = mix.data[0];
    const after = mix.data[1];
    const blank = { state: after.state, taxFree: 0, taxDeferred: 0, taxable: 0 };
    // Always render BOTH categories so the first bar stays in the same spot;
    // the second bar's values are blank until optimized, then they appear.
    const data = [before, optimized ? after : blank];
    const total = (s: { taxFree: number; taxDeferred: number; taxable: number }) =>
      s.taxFree + s.taxDeferred + s.taxable;
    const yMax = Math.ceil(Math.max(total(before), total(after)) * 1.15);

    const barChart = (
      <BarChart
        data={data}
        margin={{ top: 16, right: 8, left: 8, bottom: 22 }}
        barCategoryGap="30%"
        {...(width != null ? { width, height: 300 } : {})}
      >
        <CartesianGrid vertical={false} stroke={colors.grayLine} />
        <XAxis
          dataKey="state"
          tick={{ fontSize: 13, fill: colors.navy, fontWeight: 600 }}
          tickLine={false}
          axisLine={{ stroke: colors.grayLine }}
        >
          <Label
            value={mix.likeForLike ? 'End-of-plan balances' : 'Plan stage'}
            position="bottom"
            offset={6}
            style={{ fontSize: 11.5, fill: colors.gray, fontWeight: 600 }}
          />
        </XAxis>
        <YAxis
          domain={[0, yMax]}
          tick={{ fontSize: 11, fill: colors.gray }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v) => `$${v}M`}
        >
          <Label
            value="Savings ($M)"
            angle={-90}
            position="insideLeft"
            offset={14}
            style={{ fontSize: 11.5, fill: colors.gray, fontWeight: 600, textAnchor: 'middle' }}
          />
        </YAxis>
        <Tooltip contentStyle={ttStyle} formatter={(v, n) => [`$${v}M`, String(n)]} />
        <Bar dataKey="taxable" name="Taxable" stackId="s" fill={colors.navy} animationDuration={600} isAnimationActive={animated} />
        <Bar dataKey="taxDeferred" name="Tax-Deferred" stackId="s" fill={colors.teal} animationDuration={600} isAnimationActive={animated} />
        <Bar
          dataKey="taxFree"
          name="Tax-Free"
          stackId="s"
          fill={colors.growth}
          radius={[5, 5, 0, 0]}
          animationDuration={600}
          isAnimationActive={animated}
        />
      </BarChart>
    );

    barSection = (
      // deck-chart-block: kept whole across PDF page breaks (PrintReportView).
      <div className="deck-chart-block">
        <div style={{ fontWeight: 600, color: T.navy, fontSize: 16, marginBottom: 2 }}>
          {!optimized
            ? mix.likeForLike
              ? 'Where your money ends up, as-is'
              : 'Your current asset mix'
            : mix.likeForLike
              ? 'Where your money ends up, with and without the plan'
              : 'Your money today, and at the end of the plan'}
        </div>
        {/* Only the like-for-like pairing (both bars end-of-plan) can credit the
            difference to the plan; otherwise the gap is mostly horizon growth. */}
        <div style={{ fontSize: 13, color: T.gray, marginBottom: 16 }}>
          {!optimized
            ? mix.likeForLike
              ? 'End-of-plan balances by account type on your current course. Tap “Show optimized” to compare.'
              : 'Savings by account type. Tap “Show optimized” to see the end-of-plan bar.'
            : mix.likeForLike
              ? `Both bars are end-of-plan balances, so the difference is the plan itself — including ${fmtMoney(
                  roth.total
                )} of conversions moved into the tax-free (green) layer.`
              : `Your plan converts ${fmtMoney(
                  roth.total
                )} into the tax-free (green) layer. The end-of-plan bar also reflects growth over the plan.`}
        </div>
        {width != null ? barChart : <ResponsiveContainer width="100%" height={300}>{barChart}</ResponsiveContainer>}
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 6, fontSize: 12, color: T.gray }}>
          <LegendSwatch color={colors.growth} label="Tax-Free" />
          <LegendSwatch color={colors.teal} label="Tax-Deferred" />
          <LegendSwatch color={colors.navy} label="Taxable" />
        </div>
      </div>
    );
  }

  return (
    <div>
      {barSection}
      {/* second graph: tax-lines — always shown, independent of the toggle */}
      <RothTaxLines
        roth={roth}
        payload={payload}
        enrichment={enrichment}
        taxSeries={vm.taxSeries}
        colors={colors}
        width={width}
        standalone={!barSection}
      />
      <AsteriskNote text={CHART_DISCLAIMER} />
    </div>
  );
}

function RothTaxLines({
  roth,
  payload,
  enrichment,
  taxSeries,
  colors,
  width,
  standalone,
}: {
  roth: RothVM;
  payload: WebReportPayload;
  enrichment: ReportEnrichment | null;
  taxSeries?: TaxSeriesPoint[];
  colors: ChartColors;
  width?: number;
  standalone: boolean;
}) {
  // Real per-year cumulative-tax/rate series from SIPS (schema v8) when
  // present — real break-even too; synthesized curves for older reports.
  const { series, breakEvenYear } = useMemo(() => {
    if (taxSeries && taxSeries.length > 1) {
      return buildRothTaxSeriesFromReal(taxSeries, planStartYearOf(enrichment));
    }
    return buildRothTaxSeries({
      conversions: roth.schedule,
      taxSavings: roth.taxSavings,
      startingTotalTaxes: enrichment?.metrics.starting?.totalTaxes,
      optimizedTotalTaxes: enrichment?.metrics.optimized?.totalTaxes,
      planHorizonYears: payload.client.planHorizonYears,
      planStartYear: planStartYearOf(enrichment),
    });
  }, [roth, payload, enrichment, taxSeries]);

  const animated = width == null;
  // with-Roth = growth green (the positive outcome) vs without = neutral cool gray.
  const cWith = colors.growth;
  const cWithout = '#9DA5B1';
  const cWithFill = colors.growthSoft;
  const cWithoutFill = '#CBD0D8';

  const chart = (
    <ComposedChart
      data={series}
      margin={{ top: 16, right: 30, left: 30, bottom: 26 }}
      {...(width != null ? { width, height: 300 } : {})}
    >
      <CartesianGrid vertical={false} stroke={colors.grayLine} />
      <XAxis dataKey="year" tick={{ fontSize: 10.5, fill: colors.gray }} tickLine={false} axisLine={{ stroke: colors.grayLine }}>
        <Label value="Year" position="bottom" offset={8} style={{ fontSize: 11.5, fill: colors.gray, fontWeight: 600 }} />
      </XAxis>
      {/* left axis: effective tax rate % (headroom above the real max) */}
      <YAxis
        yAxisId="rate"
        domain={[0, (dataMax: number) => Math.max(30, Math.ceil(dataMax * 1.1))]}
        tick={{ fontSize: 10.5, fill: colors.gray }}
        tickLine={false}
        axisLine={false}
        tickFormatter={(v) => `${v}%`}
      >
        <Label
          value="Effective tax rate"
          angle={-90}
          position="insideLeft"
          offset={-12}
          style={{ fontSize: 11.5, fill: colors.gray, fontWeight: 600, textAnchor: 'middle' }}
        />
      </YAxis>
      {/* right axis: cumulative taxes $ */}
      <YAxis
        yAxisId="cum"
        orientation="right"
        tick={{ fontSize: 10.5, fill: colors.gray }}
        tickLine={false}
        axisLine={false}
        tickFormatter={(v) => `$${(Number(v) / 1_000_000).toFixed(1)}M`}
      >
        <Label
          value="Cumulative taxes paid"
          angle={90}
          position="insideRight"
          offset={-12}
          style={{ fontSize: 11.5, fill: colors.gray, fontWeight: 600, textAnchor: 'middle' }}
        />
      </YAxis>
      <Tooltip
        contentStyle={ttStyle}
        formatter={(v, n) => {
          const name = String(n);
          if (name.includes('rate')) {
            return [`${v}%`, name === 'rateWith' ? 'Tax rate (with Roth)' : 'Tax rate (without)'];
          }
          return [
            `$${(Number(v) / 1_000_000).toFixed(2)}M`,
            name === 'cumWith' ? 'Cumulative (with Roth)' : 'Cumulative (without)',
          ];
        }}
      />
      {/* cumulative taxes as filled areas */}
      <Area yAxisId="cum" type="monotone" dataKey="cumWithout" stroke="none" fill={cWithoutFill} fillOpacity={0.6} isAnimationActive={animated} />
      <Area yAxisId="cum" type="monotone" dataKey="cumWith" stroke="none" fill={cWithFill} fillOpacity={0.7} isAnimationActive={animated} />
      {/* effective tax-rate lines */}
      <Line yAxisId="rate" type="monotone" dataKey="rateWithout" stroke={cWithout} strokeWidth={2.5} strokeDasharray="6 4" dot={false} isAnimationActive={animated} />
      <Line yAxisId="rate" type="monotone" dataKey="rateWith" stroke={cWith} strokeWidth={3} dot={false} isAnimationActive={animated} />
    </ComposedChart>
  );

  return (
    <div
      className="deck-chart-block"
      style={standalone ? undefined : { marginTop: 26, borderTop: `1px solid ${T.grayLine}`, paddingTop: 18 }}
    >
      <div style={{ fontWeight: 600, color: T.navy, fontSize: 16, marginBottom: 2 }}>
        Taxes over time{breakEvenYear ? ', and where it breaks even' : ''}
      </div>
      <div style={{ fontSize: 13, color: T.gray, marginBottom: 16 }}>
        You pay more tax up front and less later.
        {breakEvenYear ? ` The lines cross, breaking even, in ${breakEvenYear}.` : ''}
      </div>
      {width != null ? chart : <ResponsiveContainer width="100%" height={300}>{chart}</ResponsiveContainer>}
      {/* break-even callout */}
      {breakEvenYear && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: T.tealSoft, borderRadius: 8, padding: '10px 14px', marginTop: 4 }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: T.teal }}>Projected break-even {breakEvenYear}</span>
          <span style={{ fontSize: 12.5, color: T.tealDeep }}>
            after this point, the conversion is pure savings
            {roth.taxSavings > 0 ? `, about ${fmtMoney(roth.taxSavings)} less in lifetime taxes` : ''}.
          </span>
        </div>
      )}
      {/* legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center', marginTop: 12, fontSize: 11.5, color: T.gray }}>
        <LegendSwatch color={cWith} label="Tax rate (with Roth)" />
        <LegendSwatch color={cWithout} label="Tax rate (without)" />
        <LegendSwatch color={cWithFill} label="Cumulative taxes (with Roth)" />
        <LegendSwatch color={cWithoutFill} label="Cumulative taxes (without)" />
      </div>
    </div>
  );
}
